import { useState, useEffect } from 'react';
import { admin } from '../lib/api';

export default function UsageDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardData, projectionData] = await Promise.all([
        admin.getUsageDashboard(),
        admin.getUsageProjection(),
      ]);
      setDashboard(dashboardData);
      setProjection(projectionData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-700 dark:text-red-400">Error: {error}</p>
            <button
              onClick={loadData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Usage & Cost Dashboard
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Monitor platform usage and operational costs
            </p>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          {['overview', 'costs', 'events'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white dark:bg-gray-700 text-accent-600 dark:text-accent-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <OverviewTab dashboard={dashboard} projection={projection} />
        )}
        {activeTab === 'costs' && (
          <CostsTab dashboard={dashboard} projection={projection} />
        )}
        {activeTab === 'events' && (
          <EventsTab events={dashboard.recentEvents} />
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, trend }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className="text-3xl opacity-50">{icon}</div>
        )}
      </div>
      {trend && (
        <div className={`mt-2 text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
}

function CostCard({ title, amount, breakdown, isTotal }) {
  return (
    <div className={`rounded-xl p-5 shadow-sm border ${
      isTotal
        ? 'bg-accent-50 dark:bg-accent-900/20 border-accent-200 dark:border-accent-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className={`text-2xl font-bold mt-1 ${isTotal ? 'text-accent-600 dark:text-accent-400' : 'text-gray-900 dark:text-white'}`}>
        ${amount}
      </p>
      {breakdown && (
        <div className="mt-3 space-y-1">
          {Object.entries(breakdown).map(([key, val]) => (
            <div key={key} className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400 capitalize">{key}</span>
              <span className="text-gray-700 dark:text-gray-300">${val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FreeTierCard({ dashboard }) {
  const freeTier = dashboard.freeTier;
  const percentUsed = parseFloat(freeTier?.today?.percentUsed || 0);
  const withinFreeTier = freeTier?.today?.withinFreeTier;

  return (
    <div className={`rounded-xl p-5 shadow-sm border ${
      withinFreeTier
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Cloudflare Free Tier Status (Today)</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            All services within free tier limits
          </p>
        </div>
        <span className={`text-2xl ${withinFreeTier ? '' : 'animate-pulse'}`}>
          {withinFreeTier ? '✅' : '⚠️'}
        </span>
      </div>

      {/* Workers AI Neurons - Primary metric */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600 dark:text-gray-400 font-medium">
            Workers AI: {freeTier?.today?.neuronsUsed?.toLocaleString() || 0} / {freeTier?.neuronsPerDay?.toLocaleString()} neurons
          </span>
          <span className={withinFreeTier ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {percentUsed}%
          </span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              percentUsed > 100 ? 'bg-red-500' :
              percentUsed > 80 ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          />
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Neurons Remaining</p>
          <p className="font-semibold text-gray-900 dark:text-white">
            {Math.round(freeTier?.today?.neuronsRemaining || 0).toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">Est. Free Extractions</p>
          <p className="font-semibold text-gray-900 dark:text-white">
            ~{freeTier?.estimatedFreeExtractions || '∞'}/day
          </p>
        </div>
      </div>

      {/* Other Free Tier Services */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Other Services</p>

        {/* Workers Requests */}
        <FreeTierMeter
          label="Workers Requests"
          used={freeTier?.workers?.requestsToday || 0}
          limit={freeTier?.workers?.requestsLimit || 100000}
          percentUsed={parseFloat(freeTier?.workers?.requestsPercentUsed || 0)}
        />

        {/* KV Reads */}
        <FreeTierMeter
          label="KV Reads"
          used={freeTier?.kv?.readsToday || 0}
          limit={freeTier?.kv?.readsLimit || 100000}
          percentUsed={parseFloat(freeTier?.kv?.readsPercentUsed || 0)}
        />

        {/* KV Writes */}
        <FreeTierMeter
          label="KV Writes"
          used={freeTier?.kv?.writesToday || 0}
          limit={freeTier?.kv?.writesLimit || 1000}
          percentUsed={parseFloat(freeTier?.kv?.writesPercentUsed || 0)}
        />

        {/* D1 Storage */}
        <FreeTierMeter
          label="D1 Storage"
          used={`${parseFloat(freeTier?.d1?.storageUsedGB || 0).toFixed(3)} GB`}
          limit={`${freeTier?.d1?.storageLimitGB || 5} GB`}
          percentUsed={parseFloat(freeTier?.d1?.storagePercentUsed || 0)}
          showRaw
        />

        {/* D1 Reads */}
        <FreeTierMeter
          label="D1 Reads (est.)"
          used={freeTier?.d1?.readsToday || 0}
          limit={freeTier?.d1?.readsLimit || 5000000}
          percentUsed={parseFloat(freeTier?.d1?.readsPercentUsed || 0)}
        />

        {/* D1 Writes */}
        <FreeTierMeter
          label="D1 Writes (est.)"
          used={freeTier?.d1?.writesToday || 0}
          limit={freeTier?.d1?.writesLimit || 100000}
          percentUsed={parseFloat(freeTier?.d1?.writesPercentUsed || 0)}
        />

        {/* Pages - Unlimited */}
        <div className="flex justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">Pages Requests</span>
          <span className="text-green-600 dark:text-green-400">Unlimited ✓</span>
        </div>

        {/* Pages Builds */}
        <div className="flex justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">Pages Builds</span>
          <span className="text-gray-600 dark:text-gray-400">
            {freeTier?.pages?.buildsPerMonth || 500}/mo
          </span>
        </div>
      </div>

      {/* Oracle Cloud (Always Free Tier) */}
      {freeTier?.oracle && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Oracle Cloud Services</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                Always Free: {freeTier.oracle.freeTier?.vms} VMs, {freeTier.oracle.freeTier?.storageGB}GB storage
              </p>
            </div>
            <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
              Free ✓
            </span>
          </div>
          {freeTier.oracle.services && (
            <div className="space-y-2 mb-3">
              {freeTier.oracle.services.map((service, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">{service.name}</span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    service.status === 'active'
                      ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                      : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
                  }`}>
                    {service.status}
                  </span>
                </div>
              ))}
            </div>
          )}
          {freeTier.oracle.advantages && (
            <ul className="text-xs text-green-600 dark:text-green-400 space-y-0.5">
              {freeTier.oracle.advantages.map((advantage, i) => (
                <li key={i}>✓ {advantage}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Apify Platform Discovery */}
      {freeTier?.apify && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Apify (Platform Discovery)</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                ${freeTier.apify.freeCreditsPerMonth}/mo free credits
              </p>
            </div>
            <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
              ~{freeTier.apify.estimatedFreeResults} results/mo
            </span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>Instagram hashtag scraping</span>
              <span>${freeTier.apify.costPerInstagramResult}/result</span>
            </div>
            <div className="flex justify-between text-gray-600 dark:text-gray-400">
              <span>TikTok hashtag scraping</span>
              <span>${freeTier.apify.costPerTikTokResult}/result</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FreeTierMeter({ label, used, limit, percentUsed, showRaw = false }) {
  const displayUsed = showRaw ? used : (typeof used === 'number' ? used.toLocaleString() : used);
  const displayLimit = showRaw ? limit : (typeof limit === 'number' ? limit.toLocaleString() : limit);

  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-600 dark:text-gray-400">
          {displayUsed} / {displayLimit}
          {percentUsed > 0 && ` (${percentUsed < 0.01 ? '<0.01' : percentUsed.toFixed(2)}%)`}
        </span>
      </div>
      <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            percentUsed > 80 ? 'bg-yellow-500' :
            percentUsed > 50 ? 'bg-blue-500' : 'bg-green-500'
          }`}
          style={{ width: `${Math.max(Math.min(percentUsed, 100), 0.5)}%` }}
        />
      </div>
    </div>
  );
}

function OverviewTab({ dashboard, projection }) {
  return (
    <div className="space-y-6">
      {/* Free Tier Status - Prominent */}
      {dashboard.freeTier && <FreeTierCard dashboard={dashboard} />}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={dashboard.overview.totalUsers}
          icon="👥"
        />
        <StatCard
          title="Total Recipes"
          value={dashboard.overview.totalRecipes}
          icon="📖"
        />
        <StatCard
          title="Today's Extractions"
          value={dashboard.today.extractions}
          subtitle={`${dashboard.today.successful} successful`}
          icon="🎯"
        />
        <StatCard
          title="This Month"
          value={dashboard.month.extractions}
          subtitle={`${dashboard.month.uniqueUsers} unique users`}
          icon="📊"
        />
      </div>

      {/* Period Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Today</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Extractions</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.today.extractions}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Success Rate</span>
              <span className="font-medium text-green-600">
                {dashboard.today.extractions > 0
                  ? ((dashboard.today.successful / dashboard.today.extractions) * 100).toFixed(0)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Audio Processed</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.today.audioMinutes} min</span>
            </div>
            {dashboard.today.frameExtraction?.videosProcessed > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Frame Extraction</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {dashboard.today.frameExtraction.videosProcessed} videos, {dashboard.today.frameExtraction.totalFrames} frames (avg {dashboard.today.frameExtraction.avgFramesPerVideo}/video)
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Neurons Used</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dashboard.today.neurons?.used?.toLocaleString() || 0} / {dashboard.today.neurons?.limit?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Actual Cost</span>
              <span className="font-medium text-green-600">${dashboard.today.costs.actual}</span>
            </div>
            {parseFloat(dashboard.today.costs.ifNoFreeTier) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 dark:text-gray-500">Without free tier</span>
                <span className="text-gray-400 dark:text-gray-500 line-through">${dashboard.today.costs.ifNoFreeTier}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Last 7 Days</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Extractions</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.week.extractions}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Success Rate</span>
              <span className="font-medium text-green-600">
                {dashboard.week.extractions > 0
                  ? ((dashboard.week.successful / dashboard.week.extractions) * 100).toFixed(0)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Audio Processed</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.week.audioMinutes} min</span>
            </div>
            {dashboard.week.frameExtraction?.videosProcessed > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Frame Extraction</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {dashboard.week.frameExtraction.videosProcessed} videos, {dashboard.week.frameExtraction.totalFrames} frames (avg {dashboard.week.frameExtraction.avgFramesPerVideo})
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Neurons Used</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dashboard.week.neurons?.used?.toLocaleString() || 0} ({dashboard.week.neurons?.percentUsed}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Actual Cost</span>
              <span className="font-medium text-green-600">${dashboard.week.costs.actual}</span>
            </div>
            {parseFloat(dashboard.week.costs.ifNoFreeTier) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 dark:text-gray-500">Without free tier</span>
                <span className="text-gray-400 dark:text-gray-500 line-through">${dashboard.week.costs.ifNoFreeTier}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">This Month ({dashboard.month.daysElapsed}/{dashboard.month.daysInMonth} days)</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Extractions</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.month.extractions}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Success Rate</span>
              <span className="font-medium text-green-600">
                {dashboard.month.extractions > 0
                  ? ((dashboard.month.successful / dashboard.month.extractions) * 100).toFixed(0)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Audio Processed</span>
              <span className="font-medium text-gray-900 dark:text-white">{dashboard.month.audioMinutes} min</span>
            </div>
            {dashboard.month.frameExtraction?.videosProcessed > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Frame Extraction</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {dashboard.month.frameExtraction.videosProcessed} videos, {dashboard.month.frameExtraction.totalFrames} frames (avg {dashboard.month.frameExtraction.avgFramesPerVideo})
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Neurons Used</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {dashboard.month.neurons?.used?.toLocaleString() || 0} ({dashboard.month.neurons?.percentUsed}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">AI Cost (Actual)</span>
              <span className="font-medium text-green-600">${dashboard.month.costs.actual}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">+ Fixed Costs</span>
              <span className="font-medium text-gray-900 dark:text-white">${dashboard.month.costs.fixed}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
              <span className="text-gray-700 dark:text-gray-300 font-medium">Total Cost</span>
              <span className="font-bold text-accent-600">${dashboard.month.costs.total}</span>
            </div>
            {parseFloat(dashboard.month.costs.ifNoFreeTier) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 dark:text-gray-500">AI cost without free tier</span>
                <span className="text-gray-400 dark:text-gray-500 line-through">${dashboard.month.costs.ifNoFreeTier}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Platform & Confidence Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Platform Breakdown (This Month)</h3>
          {dashboard.platformBreakdown.length > 0 ? (
            <div className="space-y-3">
              {dashboard.platformBreakdown.map((p) => (
                <div key={p.platform} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {p.platform === 'instagram' ? '📸' :
                       p.platform === 'tiktok' ? '🎵' :
                       p.platform === 'youtube' ? '📺' :
                       p.platform === 'facebook' ? '👤' : '🔗'}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 capitalize">{p.platform}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium text-gray-900 dark:text-white">{p.count}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({p.successRate}%)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No data yet</p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Extraction Quality (This Month)</h3>
          {dashboard.confidenceBreakdown.length > 0 ? (
            <div className="space-y-3">
              {dashboard.confidenceBreakdown.map((c) => (
                <div key={c.confidence} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      c.confidence === 'high' ? 'bg-green-500' :
                      c.confidence === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></span>
                    <span className="text-gray-700 dark:text-gray-300 capitalize">{c.confidence} Confidence</span>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">{c.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">No data yet</p>
          )}
        </div>
      </div>

      {/* Projection */}
      {projection && (
        <div className="bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl p-6 text-white">
          <h3 className="font-semibold text-lg mb-4">Cost Projection (Based on 7-Day Average)</h3>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-accent-200 text-sm">Daily Average</p>
              <p className="text-2xl font-bold">${projection.dailyAverage.variableCost}</p>
              <p className="text-accent-200 text-xs">{projection.dailyAverage.extractions} extractions</p>
            </div>
            <div>
              <p className="text-accent-200 text-sm">Monthly Projection</p>
              <p className="text-2xl font-bold">${projection.monthlyProjection.totalCost}</p>
              <p className="text-accent-200 text-xs">{projection.monthlyProjection.extractions} extractions</p>
            </div>
            <div>
              <p className="text-accent-200 text-sm">Annual Projection</p>
              <p className="text-2xl font-bold">${projection.annualProjection.totalCost}</p>
              <p className="text-accent-200 text-xs">{projection.annualProjection.extractions} extractions</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CostsTab({ dashboard, projection }) {
  return (
    <div className="space-y-6">
      {/* Cost Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CostCard
          title="Today's Variable Cost"
          amount={dashboard.today.costs.variable}
          breakdown={dashboard.today.costs.breakdown}
        />
        <CostCard
          title="Week's Variable Cost"
          amount={dashboard.week.costs.variable}
          breakdown={dashboard.week.costs.breakdown}
        />
        <CostCard
          title="Month's Variable Cost"
          amount={dashboard.month.costs.variable}
          breakdown={dashboard.month.costs.breakdown}
        />
        <CostCard
          title="Month's Total Cost"
          amount={dashboard.month.costs.total}
          breakdown={{
            variable: dashboard.month.costs.variable,
            fixed: dashboard.month.costs.fixed,
          }}
          isTotal
        />
      </div>

      {/* Cost Rates Reference */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Current Cost Rates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Whisper (per minute)</p>
            <p className="font-mono text-gray-900 dark:text-white">${dashboard.costRates.whisperPerMinute}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Llama Input (per 1M tokens)</p>
            <p className="font-mono text-gray-900 dark:text-white">${dashboard.costRates.llamaInputPerMillion}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Llama Output (per 1M tokens)</p>
            <p className="font-mono text-gray-900 dark:text-white">${dashboard.costRates.llamaOutputPerMillion}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Llama Vision (per image)</p>
            <p className="font-mono text-purple-600 dark:text-purple-400">${dashboard.costRates.llamaVisionPerImage}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">D1 Write (per 1M rows)</p>
            <p className="font-mono text-gray-900 dark:text-white">${dashboard.costRates.d1WritePerMillion}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Apify Instagram (per result)</p>
            <p className="font-mono text-blue-600 dark:text-blue-400">${dashboard.costRates.apifyInstagramPerResult}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Apify TikTok (per result)</p>
            <p className="font-mono text-blue-600 dark:text-blue-400">${dashboard.costRates.apifyTikTokPerResult}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Cobalt (Oracle Cloud)</p>
            <p className="font-mono text-green-600 dark:text-green-400">Free</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Frame Extractor (Oracle)</p>
            <p className="font-mono text-green-600 dark:text-green-400">Free</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Domain (monthly)</p>
            <p className="font-mono text-gray-900 dark:text-white">${dashboard.costRates.fixedMonthlyDomain}</p>
          </div>
        </div>
      </div>

      {/* Projections */}
      {projection && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Daily Average</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Extractions</span>
                <span className="text-gray-900 dark:text-white">{projection.dailyAverage.extractions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Audio</span>
                <span className="text-gray-900 dark:text-white">{projection.dailyAverage.audioMinutes} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Variable Cost</span>
                <span className="text-accent-600 font-medium">${projection.dailyAverage.variableCost}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Monthly Projection</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Extractions</span>
                <span className="text-gray-900 dark:text-white">{projection.monthlyProjection.extractions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Variable Cost</span>
                <span className="text-gray-900 dark:text-white">${projection.monthlyProjection.variableCost}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Fixed Cost</span>
                <span className="text-gray-900 dark:text-white">${projection.monthlyProjection.fixedCost}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <span className="text-gray-700 dark:text-gray-300 font-medium">Total</span>
                <span className="text-accent-600 font-bold">${projection.monthlyProjection.totalCost}</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Annual Projection</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Extractions</span>
                <span className="text-gray-900 dark:text-white">{projection.annualProjection.extractions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Variable Cost</span>
                <span className="text-gray-900 dark:text-white">${projection.annualProjection.variableCost}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Fixed Cost</span>
                <span className="text-gray-900 dark:text-white">${projection.annualProjection.fixedCost}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <span className="text-gray-700 dark:text-gray-300 font-medium">Total</span>
                <span className="text-accent-600 font-bold">${projection.annualProjection.totalCost}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unit Economics */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Unit Economics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-blue-700 dark:text-blue-300">Avg Cost per Extraction</p>
            <p className="font-mono text-lg text-blue-900 dark:text-blue-100">
              ${dashboard.month.extractions > 0
                ? (parseFloat(dashboard.month.costs.variable) / dashboard.month.extractions).toFixed(4)
                : '0.0000'}
            </p>
          </div>
          <div>
            <p className="text-blue-700 dark:text-blue-300">Avg Cost per User</p>
            <p className="font-mono text-lg text-blue-900 dark:text-blue-100">
              ${dashboard.month.uniqueUsers > 0
                ? (parseFloat(dashboard.month.costs.total) / dashboard.month.uniqueUsers).toFixed(2)
                : '0.00'}
            </p>
          </div>
          <div>
            <p className="text-blue-700 dark:text-blue-300">Avg Extractions/User</p>
            <p className="font-mono text-lg text-blue-900 dark:text-blue-100">
              {dashboard.month.uniqueUsers > 0
                ? (dashboard.month.extractions / dashboard.month.uniqueUsers).toFixed(1)
                : '0'}
            </p>
          </div>
          <div>
            <p className="text-blue-700 dark:text-blue-300">Success Rate</p>
            <p className="font-mono text-lg text-blue-900 dark:text-blue-100">
              {dashboard.month.extractions > 0
                ? ((dashboard.month.successful / dashboard.month.extractions) * 100).toFixed(1)
                : '0'}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsTab({ events }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Recent Extraction Events</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Last 20 events</p>
      </div>

      {events.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Platform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Audio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Confidence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(event.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {event.userEmail || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-flex items-center gap-1">
                      {event.platform === 'instagram' ? '📸' :
                       event.platform === 'tiktok' ? '🎵' :
                       event.platform === 'youtube' ? '📺' : '🔗'}
                      <span className="capitalize text-gray-700 dark:text-gray-300">{event.platform || 'unknown'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {event.audioSeconds ? `${(event.audioSeconds / 60).toFixed(1)} min` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {event.confidence && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                        event.confidence === 'high' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        event.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {event.confidence}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {event.success ? (
                      <span className="text-green-600 dark:text-green-400">✓ Success</span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">✗ {event.errorCode || 'Failed'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          No events recorded yet. Events will appear here after recipe extractions.
        </div>
      )}
    </div>
  );
}
